/**
 * Общая рамка блоков с периодом и городом: «Финансы» и «Продажи».
 *
 * Здесь — всё, что раньше было продублировано в двух блоках: период и город
 * из адреса, список городов, автоподстановка последнего месяца с данными,
 * проверка чужого города в ссылке и покрытие выгрузки. Каждое из этих
 * решений сообщается человеку словами, а не происходит молча.
 */

import { Icon } from '../components/Icon'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api'
import type { CurrentUser, DimensionValue } from '../types'
import type { FinanceContextValue } from './context'
import { humanForbidden, scopeForbidden, setScopeNames, type Catalog, type FinanceReport, type RunBody } from './data'
import { gapOf, hasMissingMonths, lagsBehind, lastLabel, partialNote, useCoverage, type Coverage, type PeriodGap } from './coverage'
import {
  defaultSelection,
  formatSelection,
  isIncomplete,
  labelOf,
  monthLabel,
  parseSelection,
  previousOf,
  rangeOf,
  trendRange,
  type PeriodSelection,
} from './period'
import { Caveat, type EmptySpec } from './parts'

export interface FrameNotice {
  key: string
  tone: 'info' | 'warn'
  text: ReactNode
  dismissible?: boolean
}

/** Какой отчёт отвечает на вопрос «за какие месяцы есть продажи». */
export function coverageSource(catalog: Catalog, order: FinanceReport[]): FinanceReport | null {
  for (const code of order) {
    if (catalog.descriptor(code)?.metrics.some((metric) => metric.key === 'revenue')) return code
  }
  return null
}

export function useBlockFrame(user: CurrentUser, catalog: Catalog, sourceOrder: FinanceReport[]) {
  const [params, setParams] = useSearchParams()
  const selection = useMemo(() => parseSelection(params.get('p')), [params])
  const rawBranch = params.get('b')
  const explicitPeriod = params.has('p')

  const patch = (changes: Record<string, string | null>) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        for (const [key, item] of Object.entries(changes)) {
          if (item === null) next.delete(key)
          else next.set(key, item)
        }
        return next
      },
      { replace: true },
    )

  // --- Города ------------------------------------------------------------------
  const [branches, setBranches] = useState<DimensionValue[] | null>(null)
  useEffect(() => {
    api
      .dimensionValues('branch')
      .then((response) => setBranches(response.values))
      // Список не отдан (403/404): обходимся без него — город из ссылки
      // не сбрасываем как «чужой», просто выбирать не из чего.
      .catch(() => setBranches(null))
  }, [])

  const restricted = Boolean(user.scope.branch?.allow)
  useEffect(() => {
    setScopeNames(restricted && branches ? branches.map((item) => item.name) : [])
  }, [restricted, branches])
  // Пока список не пришёл, город из адреса считается верным: запросы не ждут.
  const branchValid = !rawBranch || branches === null || branches.some((item) => item.guid === rawBranch)
  const branch = branchValid ? rawBranch : null
  const branchName = branch ? (branches?.find((item) => item.guid === branch)?.name ?? null) : null
  const scopeLabel =
    branchName ??
    (restricted
      ? branches && branches.length === 1
        ? branches[0]!.name
        : `Доступные города${branches?.length ? `: ${branches.map((item) => item.name).join(', ')}` : ''}`
      : 'Все города')

  // Чужой или несуществующий город в ссылке: сбросить и сказать об этом.
  // Название не показываем — у человека нет права его знать, а сервер
  // и не отдаёт названия недоступных городов.
  const [branchNotice, setBranchNotice] = useState<string | null>(null)
  useEffect(() => {
    if (!rawBranch || branches === null || branchValid) return
    const shown = restricted
      ? branches.length === 1
        ? `показан ${branches[0]!.name}`
        : `показаны ваши города${branches.length ? `: ${branches.map((item) => item.name).join(', ')}` : ''}`
      : 'показаны все города'
    setBranchNotice(
      restricted
        ? `Город из ссылки вам недоступен — ${shown}. Доступ к другим городам выдаёт администратор.`
        : `Города из ссылки нет в справочнике — ${shown}.`,
    )
    patch({ b: null })
  }, [rawBranch, branches, branchValid, restricted])

  // --- Покрытие и автоподстановка --------------------------------------------
  const source = catalog.ready ? coverageSource(catalog, sourceOrder) : null
  const coverage = useCoverage(source, 'revenue', user.period_scope?.notBefore ?? null)
  const fallbackStart = defaultSelection().start

  // Без периода в адресе — последний месяц, за который есть продажи, если
  // выгрузка отстаёт от календаря. Подстановка не молчаливая: ниже
  // появляется строка, что показан последний месяц с данными.
  useEffect(() => {
    if (explicitPeriod || !coverage.known || !coverage.last) return
    if (coverage.last < fallbackStart) patch({ p: `month:${coverage.last.slice(0, 7)}` })
  }, [explicitPeriod, coverage.known, coverage.last, fallbackStart])

  const lag = lagsBehind(coverage, fallbackStart)

  const notices: FrameNotice[] = []
  if (branchNotice) notices.push({ key: 'branch', tone: 'warn', text: branchNotice, dismissible: true })
  if (lag && coverage.last && selection.grain === 'month' && selection.start === coverage.last) {
    notices.push({
      key: 'latest',
      tone: 'info',
      text: (
        <>
          Продажи в 1С есть по {lastLabel(coverage)} включительно — показан последний месяц с данными.
          {coverage.lastPartial
            ? ` Последний документ не в конце месяца: ${monthLabel(coverage.last)}, возможно, выгружен не целиком.`
            : ' Документов позже в выгрузке пока нет.'}
        </>
      ),
    })
  }

  return {
    selection,
    branch,
    branches,
    branchName,
    restricted,
    scopeLabel,
    patch,
    coverage,
    lastSales: lag ? coverage.last : null,
    notices,
    dismissBranchNotice: () => setBranchNotice(null),
  }
}

/** Что сказать вместо сравнения с прошлым периодом, если сравнивать нечестно. */
export function noCompareReason(selection: PeriodSelection, coverage: Coverage): string | null {
  const range = rangeOf(selection)
  if (gapOf(selection, range, coverage)) return 'нет данных за период'
  if (isIncomplete(selection)) return 'период не закончен — без сравнения'
  if (hasMissingMonths(range, coverage)) return 'период загружен не целиком — без сравнения'
  if (hasMissingMonths(rangeOf(previousOf(selection)), coverage)) return 'прошлый период загружен не целиком — без сравнения'
  return null
}

/** Значение контекста блока: одно для «Финансов» и «Продаж». */
export function buildContext(
  frame: ReturnType<typeof useBlockFrame>,
  user: CurrentUser,
  catalog: Catalog,
  extra: Pick<FinanceContextValue, 'go' | 'drill' | 'receivableFilter' | 'debtsExternalOnly'>,
): FinanceContextValue {
  const { selection, branch, branchName, scopeLabel, restricted, coverage } = frame
  const range = rangeOf(selection)
  const hasBranch = (code: FinanceReport) => catalog.hasDimension(code, 'branch')
  // Ограничен по городам, а отчёт по ним не режется — сервер откажет целиком.
  // Сервер говорит об этом сам (unavailable_reason в /api/auth/me) и закрывает
  // описание такого отчёта; для старого сервера — вывод по описанию.
  const names = (frame.branches ?? []).map((item) => item.name)
  const scopeBlocked = (code: FinanceReport) => {
    const reason = user.reports.find((report) => report.code === code)?.unavailable_reason
    if (reason) return /нельзя ограничить/.test(reason) && /«Филиал»/.test(reason) ? scopeForbidden(code, names) : humanForbidden(code, reason)
    return restricted && catalog.has(code) && !hasBranch(code) ? scopeForbidden(code, names) : null
  }
  return {
    user,
    catalog,
    selection,
    range,
    previousRange: rangeOf(previousOf(selection)),
    trend: trendRange(selection),
    branch,
    branchName,
    scopeLabel,
    restricted,
    scopeBlocked,
    scoped: (code, body) => {
      const blocked = scopeBlocked(code)
      if (blocked) return { blocked }
      if (!catalog.has(code)) return null
      const filters: Record<string, string[]> = { ...(body?.filters ?? {}) }
      if (branch && hasBranch(code)) filters.branch = [branch]
      const next: RunBody = { ...body }
      if (Object.keys(filters).length > 0) next.filters = filters
      else delete next.filters
      return next
    },
    branchIgnored: (code) => Boolean(branch) && catalog.has(code) && !hasBranch(code),
    coverage,
    gap: gapOf(selection, range, coverage),
    partial: partialNote(range, coverage),
    noCompare: noCompareReason(selection, coverage),
    setSelection: (next) => frame.patch({ p: formatSelection(next) }),
    ...extra,
  }
}

/** Строки под панелью периода: что подставлено, что сброшено. */
export function FrameNotices({ notices, onDismiss }: { notices: FrameNotice[]; onDismiss: (key: string) => void }) {
  if (notices.length === 0) return null
  return (
    <div className="fin-notices">
      {notices.map((notice) => (
        <Caveat key={notice.key} tone={notice.tone}>
          <span className="fin-notice__text">{notice.text}</span>
          {notice.dismissible && (
            <button type="button" className="link-button fin-notice__close" onClick={() => onDismiss(notice.key)}>
              Понятно
            </button>
          )}
        </Caveat>
      ))}
    </div>
  )
}

/**
 * Раздел, который человеку не выдан, — карточка с объяснением, а не
 * молчаливый переход: по ссылке из письма должно быть понятно, почему
 * открылось не то.
 */
export function ClosedSection({
  title,
  sectionCode,
  reports,
  user,
  fallback,
  scopeReason,
}: {
  title: string
  /**
   * Код раздела ('finance.pnl'): причину называет сервер (section_reasons
   * в /api/auth/me). Список отчётов ниже — запасной путь для старого сервера.
   */
  sectionCode?: string
  /** Отчёты, на которых построен раздел: хотя бы один должен быть выдан. */
  reports: Array<{ code: string; title: string }>
  user: CurrentUser
  fallback: { path: string; label: string } | null
  /**
   * Отчёт выдан, но закрыт срезом прав: человек ограничен по городам,
   * а отчёт по ним не делится. Это не решение администратора скрыть раздел.
   */
  scopeReason?: string | null
}) {
  const granted = reports.filter((report) => user.reports.some((item) => item.code === report.code))
  const serverReason = sectionCode ? user.section_reasons?.[sectionCode] : undefined
  const reason = scopeReason
    ? scopeReason
    : serverReason
      ? serverReason
      : granted.length > 0
      ? `Отчёт «${granted[0]!.title}» вам выдан, но раздел скрыт администратором или в отчёте скрыты нужные показатели.`
      : reports.length === 1
        ? `Раздел строится на отчёте «${reports[0]!.title}», а он не входит в ваши права.`
        : `Раздел строится на отчётах ${reports.map((report) => `«${report.title}»`).join(', ')} — ни один не входит в ваши права.`
  return (
    <section className="panel closed-section" aria-labelledby="closed-section-title">
      <div className="panel__state">
        <Icon name="lock" size={22} className="panel__state-icon" />
        <h2 id="closed-section-title" className="closed-section__title">
          Раздел «{title}» вам не выдан
        </h2>
        <p className="small muted closed-section__text">
          {scopeReason
            ? `${reason}${user.support_contact ? ` Администратор: ${user.support_contact}.` : ''}`
            : `${reason} Открыть его может администратор системы${user.support_contact ? ` (${user.support_contact})` : ''}.`}
        </p>
        {fallback && (
          <Link className="btn btn--sm" to={fallback.path}>
            {fallback.label}
          </Link>
        )}
      </div>
    </section>
  )
}

/**
 * Пустой блок продаж: если за период нет выгрузки — так и сказано, с кнопкой
 * перехода к последнему месяцу с данными; если выгрузка есть, а продаж
 * в срезе нет — честный ноль с указанием среза.
 */
export function salesEmpty(
  ctx: FinanceContextValue,
  when: boolean,
  title = 'Продаж за период нет',
  /** Пробел другого потока (движение денег) вместо продаж. */
  override?: { gap: PeriodGap | null; noun: string },
): EmptySpec {
  const gap = override ? override.gap : ctx.gap
  const noun = override?.noun ?? 'продаж'
  if (when && gap) {
    return {
      when,
      title: gap.title,
      hint: gap.hint,
      action: gap.target && gap.targetLabel ? { label: `Показать ${gap.targetLabel}`, onClick: () => ctx.setSelection(gap.target!) } : undefined,
    }
  }
  return {
    when,
    title,
    hint: ctx.branchName
      ? `В городе «${ctx.branchName}» за «${labelOf(ctx.selection)}» ${noun} нет. Выберите другой город или период.`
      : `Выгрузка за «${labelOf(ctx.selection)}» есть, но в вашем срезе ${noun} нет.`,
  }
}
